import React, { useEffect, useRef, useState } from 'react';

import isEqual from 'react-fast-compare';
import { useIntl } from 'react-intl-next';

import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import {
	attachClosestEdge,
	type Edge,
	extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { DropIndicator } from '@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box-without-terminal';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
	draggable,
	dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { scrollJustEnoughIntoView } from '@atlaskit/pragmatic-drag-and-drop/element/scroll-just-enough-into-view';
import { Box, type XCSS, xcss } from '@atlaskit/primitives';
import { mergeRefs } from '@atlassian/eoc-schedule-common';

import { focus } from '../../../../utils/window';
import { indicatorGaps } from '../constants';
import { type ListVariant } from '../types';

import { DragHandleButton } from './drag-handle-button';
import { messages } from './messages';
import { type DraggableState, type ListItemProps } from './types';
import { getItemPosition } from './utils';

const outerStyles = xcss({
	position: 'relative',
	listStyle: 'none',
	borderRadius: 'border.radius.100',
	margin: '0',
});

const innerStyles = xcss({
	display: 'flex',
	alignItems: 'center',
	borderRadius: 'border.radius.100',
	width: '100%',
	height: '100%',
	userSelect: 'auto',
});

const draggingStyles = xcss({
	opacity: '0.6',
	backgroundColor: 'elevation.surface.hovered',
});

const disabledStyles = xcss({
	color: 'color.text.disabled',
	cursor: 'not-allowed',
});

const idleStyles = xcss({
	':hover': {
		backgroundColor: 'elevation.surface.hovered',
	},
});

const outerApperances: Record<ListVariant, XCSS> = {
	rotation: xcss({
		height: '40px',
	}),
	participant: xcss({
		height: '38px',
		boxShadow: 'elevation.shadow.raised',
	}),
};

const ListItem = <T extends {}>({
	children,
	droppableId,
	variant,
	index,
	isDisabled,
	length,
	reorder,
	title,
	value,
}: ListItemProps<T>) => {
	const intlApi = useIntl();
	const listItemRef = useRef<HTMLDivElement>(null);
	const handlerRef = useRef<HTMLDivElement>(null);
	const [closestEdge, setClosestEdge] = useState<Edge | null>(null);
	const [dragState, setDragState] = useState<DraggableState>('idle');

	useEffect(() => {
		if (!listItemRef.current || !handlerRef.current || !droppableId) {
			return;
		}

		return combine(
			draggable({
				element: listItemRef.current,
				dragHandle: handlerRef.current,
				getInitialData: () => ({ value, droppableId }),
				onGenerateDragPreview: ({ source }) => {
					scrollJustEnoughIntoView({ element: source.element });
					setDragState('generate-preview');
				},
				onDragStart: () => setDragState('dragging'),
				onDrop: () => setDragState('idle'),
			}),
			dropTargetForElements({
				element: listItemRef.current,
				canDrop: ({ source: { data } }) => data.droppableId === droppableId,
				getIsSticky: () => true,
				getData: ({ input, element }) => {
					const data = { value, droppableId };

					return attachClosestEdge(data, {
						input,
						element,
						allowedEdges: ['top', 'bottom'],
					});
				},
				onDrop: (args) => {
					setClosestEdge(null);
				},
				onDragEnter: (args) => {
					if (!isEqual(args.self.data.value, args.source.data.value)) {
						setClosestEdge(extractClosestEdge(args.self.data));
					}
				},
				onDragLeave: (args) => {
					setClosestEdge(null);
				},
				onDrag: (args) => {
					if (!isEqual(args.self.data.value, args.source.data.value)) {
						setClosestEdge(extractClosestEdge(args.self.data));
					}
				},
			}),
		);
	}, [droppableId, value]);

	const getIdentifier = (index: number) => `${index}-${title}-drag-handle`;
	const position = getItemPosition(index, length);
	const isMoveUpDisabled = position === 'first' || position === 'only';
	const isMoveDownDisabled = position === 'last' || position === 'only';

	return (
		<Box
			as="li"
			xcss={[
				outerStyles,
				outerApperances[variant],
				isDisabled && disabledStyles,
				dragState === 'dragging' && draggingStyles,
				!isDisabled && dragState !== 'dragging' && idleStyles,
			]}
		>
			<Box xcss={[innerStyles]} ref={listItemRef}>
				{droppableId && (
					<DropdownMenu
						onOpenChange={({ isOpen }) => {
							if (!isOpen) {
								focus(getIdentifier(index));
							}
						}}
						shouldRenderToParent
						trigger={({ triggerRef, ...triggerProps }) => (
							<DragHandleButton
								{...triggerProps}
								variant={variant}
								isDisabled={isDisabled}
								id={getIdentifier(index)}
								ref={mergeRefs(handlerRef, triggerRef)}
								label={intlApi.formatMessage(messages.reorder, {
									title,
									position: index + 1,
								})}
							/>
						)}
					>
						<DropdownItemGroup>
							<DropdownItemGroup>
								<DropdownItem
									onClick={() => {
										reorder(index, 0);
										focus(getIdentifier(0));
									}}
									isDisabled={isMoveUpDisabled}
								>
									{intlApi.formatMessage(messages.moveToTop)}
								</DropdownItem>
								<DropdownItem
									onClick={() => {
										reorder(index, index - 1);
										focus(getIdentifier(index - 1));
									}}
									isDisabled={isMoveUpDisabled}
								>
									{intlApi.formatMessage(messages.moveUp)}
								</DropdownItem>
								<DropdownItem
									onClick={() => {
										reorder(index, index + 1);
										focus(getIdentifier(index + 1));
									}}
									isDisabled={isMoveDownDisabled}
								>
									{intlApi.formatMessage(messages.moveDown)}
								</DropdownItem>
								<DropdownItem
									onClick={() => {
										reorder(index, length - 1);
										focus(getIdentifier(length - 1));
									}}
									isDisabled={isMoveDownDisabled}
								>
									{intlApi.formatMessage(messages.moveToBottom)}
								</DropdownItem>
							</DropdownItemGroup>
						</DropdownItemGroup>
					</DropdownMenu>
				)}
				{children}
			</Box>
			{closestEdge && <DropIndicator edge={closestEdge} gap={`${indicatorGaps[variant] / 2}px`} />}
		</Box>
	);
};

export { ListItem };
